import { useId, useState } from 'react';
import { foundation } from '../../../ui';
import SelectableCheckbox from '../../common/SelectableCheckbox';

const f = foundation;
const fc = f.color;

const titleStyle = { fontSize: '0.9375rem', fontWeight: f.fontWeight.semibold, color: fc.neutral.text, lineHeight: 1.3 };
const subStyle = { fontSize: '0.8125rem', color: fc.neutral.textSecondary, lineHeight: 1.45 };

/** True only for keyboard-driven focus (inline styles can't use :focus-visible). */
function isKeyboardFocus(el) {
  try { return el.matches(':focus-visible'); } catch (e) { return false; }
}

/**
 * Generic grey tappable acknowledgment row — ONE tappable row (tick on the
 * left, title + one line). The row is the `role="checkbox"`; the box inside is
 * the canonical `SelectableCheckbox` in presentational mode. `nudge` pulses
 * the row's ring. Shared by the recovery-code ack and the admin turn-off-2FA
 * attestation.
 */
export default function MfaAckRow({
  checked, onChange, title, sub, testId, disabled = false, nudge = false, reducedMotion = false,
}) {
  const [focusRing, setFocusRing] = useState(false);
  const titleId = useId();
  const subId = useId();
  const toggle = () => { if (!disabled) onChange?.(!checked); };

  return (
    <div
      role="checkbox"
      aria-checked={checked}
      aria-disabled={disabled || undefined}
      aria-labelledby={titleId}
      aria-describedby={sub ? subId : undefined}
      tabIndex={disabled ? -1 : 0}
      data-testid={testId}
      onClick={toggle}
      onKeyDown={(e) => {
        // Space only — the ARIA checkbox pattern (Enter is not an activator).
        if (e.key === ' ') { e.preventDefault(); toggle(); }
      }}
      onFocus={(e) => setFocusRing(isKeyboardFocus(e.currentTarget))}
      onBlur={() => setFocusRing(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        minHeight: 56,
        boxSizing: 'border-box',
        borderRadius: 12,
        backgroundColor: fc.neutral.surfaceMuted,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
        fontFamily: f.fontFamily.sans,
        outline: focusRing ? `2px solid ${fc.brand.primary}` : 'none',
        outlineOffset: 2,
        animation: nudge && !reducedMotion ? 'cvx-mfa-ack-nudge 1.2s ease 2' : 'none',
      }}
    >
      <SelectableCheckbox presentational strongBorder checked={checked} size="md" />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span id={titleId} style={titleStyle}>{title}</span>
        {sub ? <span id={subId} style={subStyle}>{sub}</span> : null}
      </span>
      <style>{`@keyframes cvx-mfa-ack-nudge { 50% { box-shadow: 0 0 0 4px ${fc.brand.primaryMuted}; } }`}</style>
    </div>
  );
}
